import {
  DownstreamConfiguration,
  Environment,
  environmentToEnum,
  Region,
  regionToEnum,
  Runtime,
  RuntimeEnvironment,
  runtimeToEnum,
} from "./definitions/types";

const HTTP_PROTOCOL = "http";
const HTTPS_PROTOCOL = "https";

export function getProtocol(downstreamConfig: DownstreamConfiguration) {
  if (downstreamConfig.protocol) {
    return downstreamConfig.protocol;
  }
  if (!downstreamConfig.environment) {
    return HTTPS_PROTOCOL;
  }
  if (downstreamConfig.environment === Environment.Local) {
    return HTTP_PROTOCOL;
  }
  return HTTPS_PROTOCOL;
}

export function getEnvironment(downstreamConfig: DownstreamConfiguration) {
  if (!downstreamConfig.environment) {
    return undefined;
  }
  if (downstreamConfig.environment === Environment.Production) {
    return undefined;
  }
  return downstreamConfig.environment.toLowerCase();
}

export function getRealm(
  environment: undefined | string,
  region: string | undefined
) {
  if (!environment && !region) {
    return "";
  }
  if (environment && !region) {
    return `${environment}.`;
  }
  if (!environment && region) {
    return `${region}.`;
  }
  return `${environment}-${region}.`;
}

export function getBaseUrl(downstreamConfig: DownstreamConfiguration): string {
  const protocol = getProtocol(downstreamConfig);
  const api = downstreamConfig.api ? `${downstreamConfig.api}.` : "www.";
  const environment = getEnvironment(downstreamConfig);
  const region = downstreamConfig.region;
  const realm = getRealm(environment, region);
  return `${protocol}://${api}${realm}twilio.com`;
}

/**
 * Utility function for determining the {@link Runtime} and {@link Environment} based on the host provided.
 * @param hostname Host name for determining the {@link RuntimeEnvironment}
 * @returns {@link RuntimeEnvironment} based on the host name
 */
export const determineRuntimeEnvironment = (
  hostname: string
): RuntimeEnvironment => {
  const hasMatchForLocalHost = hostname.match(/localhost/);

  if (hasMatchForLocalHost) {
    return {
      runtime: Runtime.Unknown,
      region: Region.Unknown,
      environment: Environment.Local,
    };
  }

  const hasMatchForRuntimeAndEnvironment = hostname.match(
    /(?<runtime>console|monkey|oneadmin\.internal|oneadmin|help|admin)\.(?<environment>dev|stage|local)-?(?<region>us1|us2|ie1|au1)?\.twilio/
  );

  if (
    hasMatchForRuntimeAndEnvironment &&
    hasMatchForRuntimeAndEnvironment.groups
  ) {
    return {
      runtime: runtimeToEnum(hasMatchForRuntimeAndEnvironment.groups.runtime),
      environment: environmentToEnum(
        hasMatchForRuntimeAndEnvironment.groups.environment
      ),
      region: regionToEnum(hasMatchForRuntimeAndEnvironment.groups?.region),
    };
  }

  const hasMatchForRuntime = hostname.match(
    /(?<runtime>console|monkey|oneadmin\.internal|oneadmin|help|admin)\.?(?<region>|ie1|au1|us1|us2)?\.twilio/
  );

  if (hasMatchForRuntime && hasMatchForRuntime.groups) {
    return {
      runtime: runtimeToEnum(hasMatchForRuntime.groups.runtime),
      environment: Environment.Production,
      region: regionToEnum(hasMatchForRuntime.groups?.region),
    };
  }

  return {
    runtime: Runtime.Unknown,
    region: Region.Unknown,
  };
};
